import React, { useState, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CustomModal from "./customModal";
import useDarkMode from '../../../hooks/useDarkMode';

export default function SupplierModal({
  showSupplierModal,
  setShowSupplierModal,
  showButton,
  onSubmit,
  setSelectedSupplier,
  selectedSupplier,
  suppliers,
  modalTitle,
  modalSubTitle,
  fetchSuppliers,
  isLoadingSuppliers = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredSuppliers, setFilteredSuppliers] = useState(suppliers);
  const [tempSelectedSupplier, setTempSelectedSupplier] = useState(selectedSupplier || null);
  const isDarkMode = useDarkMode();

  useEffect(() => {
    if (showSupplierModal && typeof fetchSuppliers === "function") {
      fetchSuppliers(); // Refetch suppliers when modal opens
    }
  }, [showSupplierModal, fetchSuppliers]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSuppliers(suppliers);
    } else {
      const filtered = suppliers.filter(supplier =>
        supplier.Supplier_Name?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        supplier.Supplier_Email?.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        supplier.Supplier_Phone?.toString().includes(searchTerm.trim())
      );
      setFilteredSuppliers(filtered);
    }
  }, [searchTerm, suppliers]);

  useEffect(() => {
    setTempSelectedSupplier(selectedSupplier || null);
  }, [selectedSupplier]);

  const handleSupplierClick = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelectedSupplier);
      setSelectedSupplier(tempSelectedSupplier);
    }
    setShowSupplierModal(false);
  };

  const handleSupplierSelection = (supplier) => {
    setTempSelectedSupplier(supplier);
  };

  const renderSupplierItem = (supplier, index) => {
    const selectedId = tempSelectedSupplier?.s_ID || tempSelectedSupplier?.S_ID;
    const supplierId = supplier.s_ID || supplier.S_ID;
    const isSelected = tempSelectedSupplier && selectedId === supplierId;

    return (
      <div
        key={supplier.s_ID || index}
        className={`supplier-item ${isSelected ? 'selected' : ''}`}
        onClick={() => handleSupplierSelection(supplier)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
          cursor: 'pointer',
          backgroundColor: isSelected
            ? (isDarkMode ? '#2d3436' : '#f8f9fa')
            : (isDarkMode ? '#1a1a1a' : '#ffffff'),
          transition: 'all 0.2s ease',
          borderRadius: isSelected ? '8px' : '0',
          margin: isSelected ? '4px 0' : '0',
          border: isSelected ? `2px solid ${isDarkMode ? '#555' : '#dee2e6'}` : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3436' : '#f8f9fa';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#ffffff';
          }
        }}
      >
        {/* Supplier Icon */}
        <div
          style={{
            width: '40px',
            height: '40px',
            background: isDarkMode ? '#555' : '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef',
            flexShrink: 0
          }}
        >
          <i className="fa fa-truck" style={{ fontSize: '16px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
        </div>

        {/* Supplier Details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: '500',
            fontSize: '14px',
            color: isDarkMode ? '#ffffff' : '#495057',
            marginBottom: '2px'
          }}>
            {supplier.s_Name || 'Unnamed Supplier'}
          </div>
          {supplier.s_Email && (
            <div style={{
              fontSize: '12px',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              marginBottom: '2px'
            }}>
              <i className="fa fa-envelope me-1"></i>
              {supplier.s_Email}
            </div>
          )}
          {supplier.s_Contact && (
            <div style={{
              fontSize: '12px',
              color: isDarkMode ? '#adb5bd' : '#6c757d'
            }}>
              <i className="fa fa-phone me-1"></i>
              {supplier.s_Contact}
            </div>
          )}
        </div>

        {/* Selection Indicator */}
        <div style={{ marginLeft: '12px', flexShrink: 0 }}>
          {isSelected && (
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#28a745',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fa fa-check" style={{ fontSize: '10px', color: 'white' }}></i>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={showSupplierModal}
      onClose={() => setShowSupplierModal(false)}
      title={modalTitle || "Select Supplier"}
      subtitle={modalSubTitle || "Choose a supplier from the list below"}
      modalSize="modal-lg"
      showButton={showButton}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText="Select"
      onSubmit={handleSupplierClick}
      submitButtonDisabled={!tempSelectedSupplier}
      isCentered={true}
    >
      {/* Search Input */}
      <div style={{ padding: '16px', borderBottom: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}` }}>
        <div className="input-group">
          <span className="input-group-text" style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
            color: isDarkMode ? '#ffffff' : '#495057'
          }}>
            <i className="fa fa-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search suppliers by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
              color: isDarkMode ? '#ffffff' : '#495057'
            }}
          />
        </div>
      </div>

      {/* Suppliers List */}
      <div style={{
        maxHeight: '400px',
        overflowY: 'auto',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff'
      }}>
        {isLoadingSuppliers ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status" style={{ color: isDarkMode ? '#ffffff' : '#6c757d' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{ color: isDarkMode ? '#ffffff' : '#6c757d', marginTop: '8px' }}>
              Loading suppliers...
            </div>
          </div>
        ) : filteredSuppliers && filteredSuppliers.length > 0 ? (
          <div>
            {filteredSuppliers.map((supplier, index) => renderSupplierItem(supplier, index))}
          </div>
        ) : (
          <div className="text-center py-4">
            <div style={{
              width: '48px',
              height: '48px',
              background: isDarkMode ? '#555' : '#f8f9fa',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
            }}>
              <i className="fa fa-truck" style={{ fontSize: '20px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
            </div>
            <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', marginBottom: '0' }}>
              {searchTerm ? 'No suppliers found matching your search' : 'No suppliers available'}
            </p>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {tempSelectedSupplier && (
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
          backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '24px',
              height: '24px',
              background: '#28a745',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '8px',
              flexShrink: 0
            }}>
              <i className="fa fa-check" style={{ fontSize: '12px', color: 'white' }}></i>
            </div>
            <div>
              <div style={{
                fontWeight: '500',
                fontSize: '14px',
                color: isDarkMode ? '#ffffff' : '#495057'
              }}>
                Selected: {tempSelectedSupplier.s_Name}
              </div>
              {tempSelectedSupplier.s_Email && (
                <div style={{
                  fontSize: '12px',
                  color: isDarkMode ? '#adb5bd' : '#6c757d'
                }}>
                  {tempSelectedSupplier.s_Email}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
}